// L5.1 — **A PAY PROMPT HAS THREE ANSWERS, AND ONE OF THEM IS "NOT YET".**
//
// HER WALK, on her own live account: tapping MY ROOM as a PAID member raised the membership/pay
// popup — **and then admitted her anyway, behind it.** Two defects in one tap, and she named both:
// a paid member met a sales panel on a surface her tier covers, and the box that appeared was
// gating nothing, because entry proceeded regardless. Dead theatre in front of an open door.
//
// **THE CLASS IS THE ONE SHE SUSPECTED, AND THIS HOUSE HAS PAID FOR IT ONCE ALREADY.** Every one of
// these decisions reads `isLoggedIn` / `isMember`, and both are derived from state that starts EMPTY
// and is filled by a request:
//
//     const isLoggedIn = Boolean(member);                       // null until restoreSession answers
//     const isMember   = Boolean(member?.subscribed) || Boolean(serverTier && serverTier !== "free");
//
// So **"she is not a member" and "we have not looked yet" are the same value** — R18's distinction,
// missing on the one question where being wrong costs a paying member a sales pitch. `InboxScreen`
// carries the fix for its OWN door in its own comment (*"that prop is false for the moment before
// the session comes back, so a DEEP LINK threw the purple membership box in the face of a paying
// Apprentice... ASK THE SERVER"*), and My Room's door was never covered by it.
//
// **THE THIRD ANSWER IS `wait`, AND IT MAY NEVER BECOME SILENCE** (L2.7's law, one surface over): a
// tap that is answered with nothing is the defect that round was about. So `wait` means REMEMBER THE
// ACTION AND RUN IT when the answer lands — which is what `pendingGateAction` and `pendingPage`
// already do — and never "drop it".
//
// This module imports NOTHING but its own types, which is the entry requirement that lets a `.mjs`
// gate LOAD and RUN it.

export const PAY_PROMPT_ANSWERS = ["open", "box", "wait"] as const;
export type PayPromptAnswer = (typeof PAY_PROMPT_ANSWERS)[number];

export type MembershipState = {
  // Has the session read ANSWERED — either way? Not "is she signed in".
  sessionKnown?: boolean;
  // Has her paid state answered? A signed-in member's tier comes from the server, so `isMember` is
  // not trustworthy until this is true.
  tierKnown?: boolean;
  isLoggedIn?: boolean;
  isMember?: boolean;
};

// **DOES THIS APP KNOW YET WHAT SHE HAS PAID FOR?**
//
// A signed-OUT visitor needs no tier read: there is no subscription to look up and the box is the
// right answer for her immediately, which is what keeps this from turning every visitor's first tap
// into a wait. A signed-IN member is not decided until the server has answered, because that is the
// only place her tier lives (Q4: what she has paid for is a fact on the server, not a flag in a
// cookie).
export function membershipKnown({ sessionKnown = false, tierKnown = false, isLoggedIn = false }: MembershipState = {}) {
  if (!sessionKnown) return false;
  if (!isLoggedIn) return true;
  return Boolean(tierKnown);
}

// What a tap on a door her TIER covers should do. `covered` is whether paying anything opens it —
// My Room, the account menu, her own room's doors — as opposed to a tier-specific feature, which is
// `requireFeature`'s question and not this one.
export function payPromptAnswer(state: MembershipState = {}): PayPromptAnswer {
  if (!membershipKnown(state)) return "wait";
  if (state.isMember) return "open";
  return "box";
}

// **THE PROPERTY HER RULING ASKS FOR, WRITTEN SO A CHECK CAN PROVE IT:** there is no state in which
// a paying member is shown a pay prompt, and no state in which the answer is nothing at all.
export function neverPromptsAPayingMember(state: MembershipState = {}) {
  return !(state.isMember === true && payPromptAnswer(state) === "box");
}
